/**
 * Class description
 *
 * Created: 2018-05-02
 * @author jonas
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IReelStoppedData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinReelEvent} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelEvent";
import {ScatterAttentionController} from "@nolimitcity/slot-game/bin/game/reelstoppresentation/scatterattention/ScatterAttentionController";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Linear, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {DungeonQuestSymbolUtil} from "../../../utils/DungeonQuestSymbolUtil";
import {DungeonQuestHelper} from "../../bigSymbols/DungeonQuestHelper";
import {DungeonQuest} from "../../DungeonQuest";
import {DungeonQuestGameAssets} from "../../DungeonQuestGameAssets";
import {DungeonQuestGameConfig, DungeonQuestGameMode, DungeonQuestSymbols} from "../../DungeonQuestGameConfig";
import {DungeonQuestSound} from "../../DungeonQuestSound";
import {DungeonQuestParsedGameData} from "../../server/data/DungeonQuestParsedGameData";


export class DungeonQuestScatterAttentionController extends ScatterAttentionController {
    private _nearWinStarted:boolean;
    private _attentionLoops:TimelineMax[] | null;
    private _attentionAnimations:TimelineSprite[] | null;
    private _normalLoops:TimelineMax[] | null;
    private _normalAnimations:TimelineSprite[] | null;
    private _fsLeft:number;

    constructor(index:number, name:string) {
        super(index, {
            numOfScattersToWin : 3,
            scattersMap : [true, false, true, false, true],
            attentionSoundName : DungeonQuestSound.SCATTER_ATTENTION,
            winAttentionSoundName : DungeonQuestSound.SCATTER_ATTENTION
        });
    }

    protected parseFeatureGameData(data:DungeonQuestParsedGameData) {
        super.parseFeatureGameData(data);
        this._fsLeft = data.freespinsRemaining;
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, (event:GameEvent) => this.onGameReady());
        EventHandler.addEventListener(this, SpinReelEvent.REEL_ON_NEAR_WIN, (event:GameEvent) => this.onNearWinSpinStart(event.params[0]));
    }

    protected playWinAttentionAnimation(data:IReelStoppedData):void {
        const tl:TimelineLite = new TimelineLite();
        const symbol3:StateReelSymbol = Reels.getSymbol(4, this._scatterPositions[4])!;
        tl.add(
            this.startAttentionLoop(symbol3)
        );

        tl.add(() => {
            this.stopAttentionLoops();
            this.stopNormalLoops();
        }, 1);

        const symbols:StateReelSymbol[] = [];
        for(let reelIndex:number = 0; reelIndex < this._scatterPositions.length; reelIndex++) {
            const symbolIndex = this._scatterPositions[reelIndex];
            if(symbolIndex < 0) {
                continue;
            }

            const symbol:StateReelSymbol = Reels.getSymbol(reelIndex, symbolIndex)!;
            if(symbol != null) {
                symbols.push(symbol);
            }
        }

        let reelHitSoundDelay:number = DungeonQuestHelper.getSymbolWindUpDuration(1);
        let startTime:number = tl.totalDuration();
        for(let i:number = 0; i < symbols.length; i++) {
            tl.add([
                DungeonQuestSymbolUtil.getSymbolStateAnimation(symbols[i], DungeonQuestGameConfig.instance.SYMBOL_STATES.win, 0.2)
            ], startTime/* + 0.1 * i*/);
        }

        tl.add([
            ()=> SlotGame.keypad.setZeroBetSpinCounter(this._fsLeft),
            () => SlotGame.sound.playEffect(DungeonQuestSound.SCATTER_WIN),
            () => DungeonQuest.soundPlayer.playWinReelHit(0),
            () => SlotGame.sound.stopEffect(DungeonQuestSound.WIN_WINDUP)
        ], startTime + 0.2 + reelHitSoundDelay);

        tl.add(() => SlotGame.sound.playEffect(DungeonQuestSound.WIN_WINDUP), startTime);


        // tl.add(DungeonQuestSymbolUtil.getSymbolStateAnimations(symbols, DungeonQuestGameConfig.instance.SYMBOL_STATES.win), 0, "start", 0.1);
        tl.add(() => this.dispatchCompleteEvent(data.reelId));

    }

    protected playAttentionAnimation(data:IReelStoppedData):void {
        const tl:TimelineLite = new TimelineLite();

        if(data.reelId == 2 && !data.quickStop) {
            this._nearWinStarted = true;
            const symbol1:StateReelSymbol = Reels.getSymbol(0, this._scatterPositions[0])!;
            const symbol2:StateReelSymbol = Reels.getSymbol(data.reelId, this._scatterPositions[data.reelId])!;
            tl.add([
                this.startAttentionLoop(symbol1),
                this.startAttentionLoop(symbol2)
            ]);
        }

        tl.add(() => this.dispatchCompleteEvent(data.reelId));
    }

    private startAttentionLoop(symbol:StateReelSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const anim:TimelineSprite = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.SCATTER_TWO_ATTENTION));
        anim.alpha = 0;
        anim.anchor.set(0.5, 0.5);
        anim.position.set(-1, -3);
        symbol.addChild(anim);

        const loop:TimelineMax = new TimelineMax({repeat : -1});
        loop.add(anim.getAnimation());


        if(this._attentionLoops == null) {
            this._attentionLoops = [];
        }
        this._attentionLoops.push(loop);

        if(this._attentionAnimations == null) {
            this._attentionAnimations = [];
        }
        this._attentionAnimations.push(anim);

        tl.add(() => loop.play());
        tl.add(new TweenLite(anim, 0.1, {alpha : 1, ease : Linear.easeNone}));
        tl.timeScale(0.2);
        return tl;
    }

    private startNormalLoop(symbol:StateReelSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const anim:TimelineSprite = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.SCATTER_LOOP));
        anim.alpha = 0;
        anim.anchor.set(0.5, 0.5);
        anim.position.set(1, 1);
        symbol.addChild(anim);

        const loop:TimelineMax = new TimelineMax({repeat : -1});
        loop.add(anim.getAnimation());


        if(this._normalLoops == null) {
            this._normalLoops = [];
        }
        this._normalLoops.push(loop);

        if(this._normalAnimations == null) {
            this._normalAnimations = [];
        }
        this._normalAnimations.push(anim);

        tl.add(() => loop.play());
        tl.add(new TweenLite(anim, 0.1, {alpha : 1, ease : Linear.easeNone}));
        return tl;
    }

    private stopAttentionLoops() {
        const tl:TimelineLite = new TimelineLite();
        if(this._attentionAnimations) {
            for(let anim of this._attentionAnimations) {
                tl.add(new TweenLite(anim, 0.2, {alpha : 0, ease : Linear.easeNone}), 0);
                tl.add(() => {
                    if(anim.parent) {
                        anim.parent.removeChild(anim)
                    }
                });
            }
            this._attentionAnimations = null;
        }

        if(this._attentionLoops) {
            for(let loop of this._attentionLoops) {
                tl.add(() => loop.pause());
                tl.add(() => loop.kill());
            }
            this._attentionLoops = null;
        }
    }

    private stopNormalLoops() {
        const tl:TimelineLite = new TimelineLite();
        if(this._normalAnimations) {
            for(let anim of this._normalAnimations) {
                tl.add(new TweenLite(anim, 0.1, {alpha : 0, ease : Linear.easeNone}), 0);
                tl.add(() => {
                    if(anim.parent) {
                        anim.parent.removeChild(anim)
                    }
                });
            }
            this._normalAnimations = null;
        }
        if(this._normalLoops) {
            for(let loop of this._normalLoops) {
                tl.add(() => loop.pause());
                tl.add(() => loop.kill());
            }
            this._normalLoops = null;
        }
    }


    public onReelBounce(data:IReelStoppedData):void {
        if(data.reelId == 3 && this._nearWinStarted) {
            DungeonQuest.reelAreaEffectController.switchBackground(DungeonQuestGameMode.FREESPIN, 5);
        }

        if(data.reelId == 4 && this._nearWinStarted) {
            this._nearWinStarted = false;
            if(!this._freespinTriggered) {
                SlotGame.sound.playEffect(DungeonQuestSound.ANTICIPATION_SCATTER_FAIL);
                DungeonQuest.reelAreaEffectController.switchBackground(DungeonQuestGameMode.NORMAL, 1);
                this.stopAttentionLoops();
            }

        }

        //Add looping animation to symbol.
        if(this._scatterPositions[data.reelId] >= 0) {
            const tl:TimelineLite = new TimelineLite();
            const symbol:StateReelSymbol = Reels.getSymbol(data.reelId, this._scatterPositions[data.reelId])!;
            tl.add(this.startNormalLoop(symbol));
        }
        super.onReelBounce(data);
    }

    private onNearWinSpinStart(reelId:number) {
        if(this._gameData!.mode != DungeonQuestGameMode.RANDOM_WILD_TRANSFORM && this._gameData!.mode != DungeonQuestGameMode.RANDOM_TRANSFORM && this._gameData!.mode != DungeonQuestGameMode.FREESPIN) {
            this._nearWinStarted = true;
        }
    }


    private onGameReady() {
        //To start the animation on S symbols on startup.
        for(let reelIndex:number = 0; reelIndex < 5; reelIndex++) {
            for(let symIndex:number = 0; symIndex < 7; symIndex++) {
                const symbol = Reels.getSymbol(reelIndex, symIndex);
                if(symbol) {
                    if(symbol.symName == DungeonQuestSymbols.S) {
                        this.startNormalLoop(symbol);
                    }
                }
            }
        }

    }
}